import { Injectable, Inject, OnDestroy } from '@angular/core';
import { ClientAutocomplete } from './client-autocomplete';
import { BehaviorSubject, Subscription, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '@morningstar/shared/environment';
import { BaseService } from '@morningstar/shared/base/base-service';
import { StorageService } from '@morningstar/shared/base/storage-service';
import { StorageTypes } from '@morningstar/shared/base/storage-service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { filter, distinctUntilChanged, tap } from 'rxjs/operators';

@Injectable()
export class ClientAutocompleteService extends BaseService implements OnDestroy {

  private filterableList: ClientAutocomplete[];
  private list = new BehaviorSubject<ClientAutocomplete[]>([]);
  list$ = this.list.asObservable();

  private storedClientSub: Subscription;
  private storedClient = new BehaviorSubject<ClientAutocomplete>(null);
  storedClient$ = this.storedClient.asObservable();

  clientChanged = new Subject<ClientAutocomplete>();
  clientChanged$ = this.clientChanged.asObservable();

  isActive$ = this.storageService.clientListIsActive$;
  currentLocation: string;

  constructor(private httpClient: HttpClient,
              private storageService: StorageService,
              @Inject('API_BASE_URL') private baseUrl: string,
              protected snackbar: MatSnackBar) {
    super(snackbar);

    this.storedClientSub = this.storageService.client$.pipe(
      filter(client => client != null),
      distinctUntilChanged(),
      tap(client => this.setStoredClient(client, false))
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.storedClientSub.unsubscribe();
  }

  getList(location: string, tenantId: string, forceSettingClient: boolean = false){
    const endpoint = `${this.baseUrl}${environment.api.client.autocompleteList}`
                      .replace('{location}', location)
                      .replace('{isActive}', this.storageService.getItem(StorageTypes.clientListIsActive) || 'true');

    this.setProcessing(true);
    this.httpClient.get<ClientAutocomplete[]>(endpoint)
                   .subscribe(list => {
                     if (list && list.length > 0){
                       this.filterableList = list;

                       const isFutureClient: boolean = this.storageService.getFutureClient();
                       this.filter("", isFutureClient);

                       if (tenantId || forceSettingClient) {
                         this.setStoredClient(tenantId);
                       }
                       else {
                        if (this.filterableList.length > 0) {
                          this.setStoredClient(this.filterableList[0].tenantId);
                        }
                       }
                     } else if (list.length === 0 && this.storageService.getItem(StorageTypes.clientListIsActive) === 'false') {
                       this.storageService.setItem(StorageTypes.clientListIsActive, 'true');
                       this.setSnackbarMessage('No inactive clients at this location');
                       this.getList(location, null);
                     } else if (list.length === 0) {
                       this.filterableList = [];
                       this.list.next([]);
                       this.storedClient.next(null);
                       this.setSnackbarMessage('No clients at this location');
                     }

                     this.setProcessing(false);
                   }, error => {
                     this.setErrorMessage('Error retrieving list of clients');
                     this.setProcessing(false);
                  });
  }

  setStoredClient(storedClientId: string, setStorageValue: boolean = true){
    if (this.filterableList && this.filterableList.length > 0){

      if (!storedClientId){
        storedClientId = this.storageService.getItem(StorageTypes.client);
      }

      let client: ClientAutocomplete;

      if (storedClientId){
        client = this.findClient(storedClientId);
      }

      if (!storedClientId || !client){
        client = this.filterableList[0];
      }

      if (setStorageValue) {
        this.storageService.setItem(StorageTypes.client, client.tenantId);
      }

      this.storedClient.next(client);
    }
  }

  sortList(leftSide: ClientAutocomplete, rightSide: ClientAutocomplete): number{
    return leftSide.sortName.localeCompare(rightSide.sortName);
  }

  filter(value: string, futureClient: boolean = false) {
    if (typeof value === 'string') {
      const filterValue = value.toLowerCase();

      if (this.filterableList && this.filterableList.length > 0) {
        if (filterValue !== "") {
          this.list.next(this.filterableList.filter(client => client.sortName.toLowerCase().includes(filterValue))
                                            .sort(this.sortList));
        } else {
          const filteredList = this.filterableList.filter(client => client.futureClient === futureClient)
                                                .filter(client => client.sortName.toLowerCase().includes(filterValue))
                                                .sort(this.sortList);
          this.list.next(filteredList);

          this.filterableList = filteredList;
        }
      } else {
        this.list.next([]);
      }
    }
  }

  setIsActive(isActive: boolean, tenantId: string = null) {
    this.storageService.removeItem(StorageTypes.clientListIsActive); // remove it first
    this.storageService.setItem(StorageTypes.clientListIsActive, isActive ? 'true' : 'false');
    this.getList(this.storageService.location.getValue(), tenantId, tenantId ? true : false);
  }

  findClient(tenantId: string) {
    return this.filterableList.find((element: ClientAutocomplete) => {
      return element.tenantId === tenantId;
    });
  }

  setFutureClients(isFutureClient: boolean, tenantId: string = null) {
    this.storageService.removeItem(StorageTypes.clientListIsActive); // remove it first
    this.storageService.setItem(StorageTypes.futureClients, isFutureClient ? 'true' : 'false');
    this.getList(this.storageService.location.getValue(), tenantId, tenantId ? true : false);
  }
}
